import { createBaseApi } from '../baseApi';
// Import orderItemApi to update individual order item caches
import { orderItemApi } from './orderItemApi';

// Create the specific API instance for fulfillment groups
export const enhancedFulfillmentGroupApi = createBaseApi('fulfillmentGroupApi', ['FulfillmentGroup', 'Request', 'Order', 'OrderItem']);

export const fulfillmentGroupApi = enhancedFulfillmentGroupApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get fulfillment group details
    getFulfillmentGroupDetails: builder.query({
      query: (fulfillmentGroupId) => `/fulfillment-groups/${fulfillmentGroupId}`,
      providesTags: (result, error, fulfillmentGroupId) => [
        { type: 'FulfillmentGroup', id: fulfillmentGroupId },
        { type: 'FulfillmentGroup', id: 'LIST' },
      ],
    }),

    // Cancel fulfillment group
    cancelFulfillmentGroup: builder.mutation({
      query: ({ orderId, fulfillmentGroupId, cancelReason, cancelComments }) => ({
        url: `/orders/${orderId}/fulfillment-groups/${fulfillmentGroupId}/cancel`,
        method: 'PUT',
        body: { cancelReason, cancelComments }
      }),
      async onQueryStarted({ orderId, fulfillmentGroupId }, { dispatch, queryFulfilled, getState }) {
        // First, get the current fulfillment group data to know which order items to update
        const currentFGData = fulfillmentGroupApi.endpoints.getFulfillmentGroupDetails.select(fulfillmentGroupId)(getState());
        
        // Optimistic update for the fulfillment group details
        const fgPatchResult = dispatch(
          fulfillmentGroupApi.util.updateQueryData('getFulfillmentGroupDetails', fulfillmentGroupId, (draft) => {
            if (draft) {
              draft.status = 'canceled';
              // Also update any order items to canceled status
              if (draft.orderItems) {
                draft.orderItems.forEach(item => {
                  if (item.status !== 'canceled') {
                    item.status = 'canceled';
                  }
                });
              }
            }
          })
        );

        // Optimistically update individual order item queries
        const orderItemPatches = [];
        if (currentFGData?.data?.orderItems) {
          currentFGData.data.orderItems.forEach(item => {
            if (item.id && item.status !== 'canceled') {
              // Use orderItemApi for updating individual order item queries
              const orderItemPatch = dispatch(
                orderItemApi.util.updateQueryData('getOrderItemDetails', item.id, (draft) => {
                  if (draft) {
                    draft.status = 'canceled';
                  }
                })
              );
              orderItemPatches.push(orderItemPatch);
            }
          });
        }
        
        try {
          await queryFulfilled;
        } catch {
          // Revert optimistic updates if mutation fails
          fgPatchResult.undo();
          orderItemPatches.forEach(patch => patch.undo());
        }
      },
      invalidatesTags: (result, error, { fulfillmentGroupId, orderId }) => [
        { type: 'FulfillmentGroup', id: fulfillmentGroupId },
        { type: 'FulfillmentGroup', id: 'LIST' },
        { type: 'Order', id: orderId },
        { type: 'Order', id: 'LIST' },
        { type: 'OrderItem', id: 'LIST' },
      ],
    }),

    // Confirm fulfillment group delivery
    confirmFulfillmentGroup: builder.mutation({
      query: ({ orderId, fulfillmentGroupId }) => ({
        url: `/orders/${orderId}/fulfillment-groups/${fulfillmentGroupId}/confirm`,
        method: 'PUT',
      }),
      async onQueryStarted({ orderId, fulfillmentGroupId }, { dispatch, queryFulfilled, getState }) {
        // Get the current fulfillment group data to know which order items to update
        const currentFGData = fulfillmentGroupApi.endpoints.getFulfillmentGroupDetails.select(fulfillmentGroupId)(getState());
        
        // Optimistic update for fulfillment group
        const fgPatchResult = dispatch(
          fulfillmentGroupApi.util.updateQueryData('getFulfillmentGroupDetails', fulfillmentGroupId, (draft) => {
            if (draft) {
              draft.status = 'delivery_completed_to_customer';
              // Also update any order items to delivered status
              if (draft.orderItems) {
                draft.orderItems.forEach(item => {
                  if (item.status === 'shipped' || item.status === 'delivery_in_progress') {
                    item.status = 'delivered';
                  }
                });
              }
            }
          })
        );

        // Optimistically update individual order item queries
        const orderItemPatches = [];
        if (currentFGData?.data?.orderItems) {
          currentFGData.data.orderItems.forEach(item => {
            if (item.id && (item.status === 'shipped' || item.status === 'delivery_in_progress')) {
              const orderItemPatch = dispatch(
                orderItemApi.util.updateQueryData('getOrderItemDetails', item.id, (draft) => {
                  if (draft) {
                    draft.status = 'delivered';
                  }
                })
              );
              orderItemPatches.push(orderItemPatch);
            }
          });
        }
        
        try {
          await queryFulfilled;
        } catch {
          // Revert optimistic updates if mutation fails
          fgPatchResult.undo();
          orderItemPatches.forEach(patch => patch.undo());
        }
      },
      invalidatesTags: (result, error, { fulfillmentGroupId, orderId }) => [
        { type: 'FulfillmentGroup', id: fulfillmentGroupId },
        { type: 'FulfillmentGroup', id: 'LIST' },
        { type: 'Order', id: orderId },
        { type: 'Order', id: 'LIST' },
        { type: 'OrderItem', id: 'LIST' },
      ],
    }),

    // Request group return (customer action)
    requestGroupReturn: builder.mutation({
      query: ({ fulfillmentGroupId, reason, comments }) => ({
        url: `/requests/group-return`,
        method: 'POST',
        body: { fulfillmentGroupId, reason, comments }
      }),
      invalidatesTags: (result, error, { fulfillmentGroupId }) => [
        { type: 'FulfillmentGroup', id: fulfillmentGroupId },
        { type: 'FulfillmentGroup', id: 'LIST' },
        { type: 'OrderItem', id: 'LIST' },
        { type: 'Request', id: 'LIST' },
        { type: 'Order', id: 'LIST' },
      ],
    }),

    // Get return requests for fulfillment group
    getReturnRequests: builder.query({
      query: (fulfillmentGroupId) => `/fulfillment-groups/${fulfillmentGroupId}/return-requests`,
      providesTags: (result, error, fulfillmentGroupId) => [
        { type: 'Request', id: 'LIST' },
        { type: 'FulfillmentGroup', id: fulfillmentGroupId },
      ],
    }),

    // Get tracking information for fulfillment group
    getTracking: builder.query({
      query: (fulfillmentGroupId) => `/fulfillment-groups/${fulfillmentGroupId}/tracking`,
      providesTags: (result, error, fulfillmentGroupId) => [
        { type: 'FulfillmentGroup', id: fulfillmentGroupId },
      ],
    }),

    // Cancel group return (customer action)
    cancelGroupReturn: builder.mutation({
      query: ({ fulfillmentGroupId }) => ({
        url: `/fulfillment-groups/${fulfillmentGroupId}/cancel-group-return`,
        method: 'PUT',
      }),
      async onQueryStarted({ fulfillmentGroupId }, { dispatch, queryFulfilled, getState }) {
        // Get the current fulfillment group data to know which order items to update
        const currentFGData = fulfillmentGroupApi.endpoints.getFulfillmentGroupDetails.select(fulfillmentGroupId)(getState());
        
        // Optimistic update for fulfillment group
        const fgPatchResult = dispatch(
          fulfillmentGroupApi.util.updateQueryData('getFulfillmentGroupDetails', fulfillmentGroupId, (draft) => {
            if (draft) {
              draft.status = 'delivery_completed_to_customer';
              // Update order items that were in return_requested status
              if (draft.orderItems) {
                draft.orderItems.forEach(item => {
                  if (item.status === 'return_requested_by_customer') {
                    item.status = 'delivered';
                    item.returnRequest = null;
                  }
                });
              }
            }
          })
        );

        // Optimistically update individual order item queries
        const orderItemPatches = [];
        if (currentFGData?.data?.orderItems) {
          currentFGData.data.orderItems.forEach(item => {
            if (item.id && item.status === 'return_requested_by_customer') {
              const orderItemPatch = dispatch(
                orderItemApi.util.updateQueryData('getOrderItemDetails', item.id, (draft) => {
                  if (draft) {
                    draft.status = 'delivered';
                    draft.returnRequest = null;
                  }
                })
              );
              orderItemPatches.push(orderItemPatch);
            }
          });
        }
        
        try {
          await queryFulfilled;
        } catch {
          fgPatchResult.undo();
          orderItemPatches.forEach(patch => patch.undo());
        }
      },
      invalidatesTags: (result, error, { fulfillmentGroupId }) => [
        { type: 'FulfillmentGroup', id: fulfillmentGroupId },
        { type: 'FulfillmentGroup', id: 'LIST' },
        { type: 'Request', id: 'LIST' },
        { type: 'Order', id: 'LIST' },
        { type: 'OrderItem', id: 'LIST' },
      ],
    }),

    // Approve group return (merchant action)
    approveGroupReturn: builder.mutation({
      query: ({ fulfillmentGroupId, approvalComments }) => ({
        url: `/fulfillment-groups/${fulfillmentGroupId}/approve-group-return`,
        method: 'PUT',
        body: { approvalComments }
      }),
      async onQueryStarted({ fulfillmentGroupId, approvalComments }, { dispatch, queryFulfilled, getState }) {
        // Get the current fulfillment group data to know which order items to update
        const currentFGData = fulfillmentGroupApi.endpoints.getFulfillmentGroupDetails.select(fulfillmentGroupId)(getState());
        
        // Optimistic update for fulfillment group
        const fgPatchResult = dispatch(
          fulfillmentGroupApi.util.updateQueryData('getFulfillmentGroupDetails', fulfillmentGroupId, (draft) => {
            if (draft && draft.orderItems) {
              const returnRequestedItems = draft.orderItems.filter(item => 
                item.status === 'return_requested_by_customer'
              );
              
              if (returnRequestedItems.length > 0) {
                // Update status based on whether all items are being returned
                const allItemsReturning = draft.orderItems.every(item => 
                  item.status === 'return_requested_by_customer'
                );
                
                draft.status = allItemsReturning ? 'group_return_approved' : 'partial_return_approved';
                
                // Update individual item statuses
                returnRequestedItems.forEach(item => {
                  item.status = 'return_approved_by_merchant';
                  if (item.returnRequest) {
                    item.returnRequest.approvalComments = approvalComments;
                    item.returnRequest.approvedAt = new Date().toISOString();
                  }
                });
              }
            }
          })
        );

        // Optimistically update individual order item queries
        const orderItemPatches = [];
        if (currentFGData?.data?.orderItems) {
          const returnRequestedItems = currentFGData.data.orderItems.filter(item => 
            item.status === 'return_requested_by_customer'
          );
          
          returnRequestedItems.forEach(item => {
            if (item.id) {
              const orderItemPatch = dispatch(
                orderItemApi.util.updateQueryData('getOrderItemDetails', item.id, (draft) => {
                  if (draft) {
                    draft.status = 'return_approved_by_merchant';
                    if (draft.returnRequest) {
                      draft.returnRequest.approvalComments = approvalComments;
                      draft.returnRequest.approvedAt = new Date().toISOString();
                    }
                  }
                })
              );
              orderItemPatches.push(orderItemPatch);
            }
          });
        }
        
        try {
          await queryFulfilled;
        } catch {
          fgPatchResult.undo();
          orderItemPatches.forEach(patch => patch.undo());
        }
      },
      invalidatesTags: (result, error, { fulfillmentGroupId }) => [
        { type: 'FulfillmentGroup', id: fulfillmentGroupId },
        { type: 'FulfillmentGroup', id: 'LIST' },
        { type: 'Request', id: 'LIST' },
        { type: 'Order', id: 'LIST' },
        { type: 'OrderItem', id: 'LIST' },
      ],
    }),

    // Reject group return (merchant action)
    rejectGroupReturn: builder.mutation({
      query: ({ fulfillmentGroupId, rejectionReason }) => ({
        url: `/fulfillment-groups/${fulfillmentGroupId}/reject-group-return`,
        method: 'PUT',
        body: { rejectionReason }
      }),
      async onQueryStarted({ fulfillmentGroupId, rejectionReason }, { dispatch, queryFulfilled, getState }) {
        // Get the current fulfillment group data to know which order items to update
        const currentFGData = fulfillmentGroupApi.endpoints.getFulfillmentGroupDetails.select(fulfillmentGroupId)(getState());
        
        // Optimistic update for fulfillment group
        const fgPatchResult = dispatch(
          fulfillmentGroupApi.util.updateQueryData('getFulfillmentGroupDetails', fulfillmentGroupId, (draft) => {
            if (draft) {
              draft.status = 'delivery_completed_to_customer';
              // Update order items that were in return_requested status
              if (draft.orderItems) {
                draft.orderItems.forEach(item => {
                  if (item.status === 'return_requested_by_customer') {
                    item.status = 'delivered';
                    if (item.returnRequest) {
                      item.returnRequest.rejectionReason = rejectionReason;
                      item.returnRequest.rejectedAt = new Date().toISOString();
                      item.returnRequest.status = 'rejected';
                    }
                  }
                });
              }
            }
          })
        );

        // Optimistically update individual order item queries
        const orderItemPatches = [];
        if (currentFGData?.data?.orderItems) {
          currentFGData.data.orderItems.forEach(item => {
            if (item.id && item.status === 'return_requested_by_customer') {
              const orderItemPatch = dispatch(
                orderItemApi.util.updateQueryData('getOrderItemDetails', item.id, (draft) => {
                  if (draft) {
                    draft.status = 'delivered';
                    if (draft.returnRequest) {
                      draft.returnRequest.rejectionReason = rejectionReason;
                      draft.returnRequest.rejectedAt = new Date().toISOString();
                      draft.returnRequest.status = 'rejected';
                    }
                  }
                })
              );
              orderItemPatches.push(orderItemPatch);
            }
          });
        }
        
        try {
          await queryFulfilled;
        } catch {
          fgPatchResult.undo();
          orderItemPatches.forEach(patch => patch.undo());
        }
      },
      invalidatesTags: (result, error, { fulfillmentGroupId }) => [
        { type: 'FulfillmentGroup', id: fulfillmentGroupId },
        { type: 'FulfillmentGroup', id: 'LIST' },
        { type: 'Request', id: 'LIST' },
        { type: 'Order', id: 'LIST' },
        { type: 'OrderItem', id: 'LIST' },
      ],
    }),

    // Approve all returns in group (merchant action)
    approveAllReturns: builder.mutation({
      query: ({ fulfillmentGroupId, approvalComments, usageFee, returnToAddress }) => ({
        url: `/fulfillment-groups/${fulfillmentGroupId}/approve-all-returns`,
        method: 'PUT',
        body: { 
          approvalComments,
          usageFee,
          returnToAddress 
        }
      }),
      transformResponse: (response) => {
        console.log('Transform response for approveAllReturns:', response);
        return response;
      },
      async onQueryStarted({ fulfillmentGroupId }, { dispatch, queryFulfilled, getState }) {
        // Get current FG data to properly update order items
        const currentFGData = fulfillmentGroupApi.endpoints.getFulfillmentGroupDetails.select(fulfillmentGroupId)(getState());
        
        // Optimistic update for the FG
        const fgPatch = dispatch(
          fulfillmentGroupApi.util.updateQueryData('getFulfillmentGroupDetails', fulfillmentGroupId, draft => {
            if (draft) {
              draft.status = 'return_approved';
              // Update all return-requested items to approved
              draft.orderItems?.forEach(item => {
                if (item.status === 'return_requested_by_customer') {
                  item.status = 'return_approved_by_merchant';
                }
              });
            }
          })
        );
        
        // Optimistic updates for individual order items
        const orderItemPatches = [];
        if (currentFGData?.data?.orderItems) {
          currentFGData.data.orderItems.forEach(item => {
            if (item.id && item.status === 'return_requested_by_customer') {
              const patch = dispatch(
                orderItemApi.util.updateQueryData('getOrderItemDetails', item.id, draft => {
                  if (draft) {
                    draft.status = 'return_approved_by_merchant';
                  }
                })
              );
              orderItemPatches.push(patch);
            }
          });
        }
        
        try {
          // Wait for the server response
          const result = await queryFulfilled;
          console.log('approveAllReturns fulfilled with result:', result);
          
          // No additional updates needed since we invalidate the tags
        } catch (error) {
          console.error('Error in approveAllReturns:', error);
          // Undo optimistic updates on error
          fgPatch.undo();
          orderItemPatches.forEach(p => p.undo());
        }
      },
      invalidatesTags: (result, error, { fulfillmentGroupId }) => [
        { type: 'FulfillmentGroup', id: fulfillmentGroupId },
        { type: 'FulfillmentGroup', id: 'LIST' },
        { type: 'Request', id: 'LIST' },
        { type: 'Order', id: 'LIST' },
        { type: 'OrderItem', id: 'LIST' },
      ],
    }),

    // Reject all returns in group (merchant action)
    rejectAllReturns: builder.mutation({
      query: ({ fulfillmentGroupId, rejectionReason }) => ({
        url: `/fulfillment-groups/${fulfillmentGroupId}/reject-all-returns`,
        method: 'PUT',
        body: { rejectionReason }
      }),
      async onQueryStarted({ fulfillmentGroupId, rejectionReason }, { dispatch, queryFulfilled, getState }) {
        const currentFGData = fulfillmentGroupApi.endpoints.getFulfillmentGroupDetails.select(fulfillmentGroupId)(getState());

        // 1) Patch FG status locally
        const fgPatch = dispatch(
          fulfillmentGroupApi.util.updateQueryData('getFulfillmentGroupDetails', fulfillmentGroupId, draft => {
            if (draft) {
              draft.status = 'return_rejected';
              draft.orderItems?.forEach(item => {
                if (item.status === 'return_requested_by_customer') {
                  item.status = 'return_rejected_by_merchant';
                  if (item.returnRequest) {
                    item.returnRequest.rejectionReason = rejectionReason;
                    item.returnRequest.rejectedAt = new Date().toISOString();
                  }
                }
              });
            }
          })
        );

        const orderItemPatches = [];
        if (currentFGData?.data?.orderItems) {
          currentFGData.data.orderItems.forEach(item => {
            if (item.id && item.status === 'return_requested_by_customer') {
              const patch = dispatch(
                orderItemApi.util.updateQueryData('getOrderItemDetails', item.id, draft => {
                  if (draft) {
                    draft.status = 'return_rejected_by_merchant';
                    if (draft.returnRequest) {
                      draft.returnRequest.rejectionReason = rejectionReason;
                      draft.returnRequest.rejectedAt = new Date().toISOString();
                    }
                  }
                })
              );
              orderItemPatches.push(patch);
            }
          });
        }

        try {
          await queryFulfilled;
        } catch {
          fgPatch.undo();
          orderItemPatches.forEach(p => p.undo());
        }
      },
      invalidatesTags: (result, error, { fulfillmentGroupId }) => [
        { type: 'FulfillmentGroup', id: fulfillmentGroupId },
        { type: 'FulfillmentGroup', id: 'LIST' },
        { type: 'Request', id: 'LIST' },
        { type: 'Order', id: 'LIST' },
        { type: 'OrderItem', id: 'LIST' },
      ],
    }),

    // Agree to pickup (merchant action)
    agreePickup: builder.mutation({
      query: ({ fulfillmentGroupId, pickupLocationAddress }) => ({
        url: `/fulfillment-groups/${fulfillmentGroupId}/agree-pickup`,
        method: 'PUT',
        body: { pickupLocationAddress }
      }),
      invalidatesTags: (result, error, { fulfillmentGroupId }) => [
        { type: 'FulfillmentGroup', id: fulfillmentGroupId },
        { type: 'FulfillmentGroup', id: 'LIST' },
        { type: 'Order', id: 'LIST' },
        { type: 'OrderItem', id: 'LIST' },
      ],
    }),

    // Confirm pickup (customer action)
    confirmPickup: builder.mutation({
      query: ({ fulfillmentGroupId, pickupCode }) => ({
        url: `/fulfillment-groups/${fulfillmentGroupId}/confirm-pickup`,
        method: 'PUT',
        body: { pickupCode }
      }),
      invalidatesTags: (result, error, { fulfillmentGroupId }) => [
        { type: 'FulfillmentGroup', id: fulfillmentGroupId },
        { type: 'FulfillmentGroup', id: 'LIST' },
        { type: 'Order', id: 'LIST' },
        { type: 'OrderItem', id: 'LIST' },
      ],
    }),

    // Mark return shipped (customer action)
    markReturnShipped: builder.mutation({
      query: ({ fulfillmentGroupId }) => ({
        url: `/fulfillment-groups/${fulfillmentGroupId}/mark-return-shipped`,
        method: 'PUT'
      }),
      async onQueryStarted({ fulfillmentGroupId }, { dispatch, queryFulfilled, getState }) {
        const currentFG = fulfillmentGroupApi.endpoints.getFulfillmentGroupDetails.select(fulfillmentGroupId)(getState());
        const fgPatch = dispatch(
          fulfillmentGroupApi.util.updateQueryData('getFulfillmentGroupDetails', fulfillmentGroupId, draft => {
            if (draft) {
              draft.status = 'return_shipped';
              draft.orderItems?.forEach(item => {
                if (item.status === 'return_approved_by_merchant') {
                  item.status = 'return_shipped_to_merchant';
                }
              });
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          fgPatch.undo();
        }
      },
      invalidatesTags: (r,e,{fulfillmentGroupId})=>[
        {type:'FulfillmentGroup',id:fulfillmentGroupId},
        {type:'FulfillmentGroup',id:'LIST'},
        {type:'OrderItem',id:'LIST'}
      ]
    }),

    // Confirm return delivery (merchant action)
    confirmReturnDelivery: builder.mutation({
      query: ({ fulfillmentGroupId, deliveryComments }) => ({
        url: `/fulfillment-groups/${fulfillmentGroupId}/confirm-return-delivery`,
        method: 'POST',
        body: { deliveryComments }
      }),
      async onQueryStarted({ fulfillmentGroupId }, { dispatch, queryFulfilled, getState }) {
        const currentFG = fulfillmentGroupApi.endpoints.getFulfillmentGroupDetails.select(fulfillmentGroupId)(getState());
        
        // Optimistic update for fulfillment group
        const fgPatch = dispatch(
          fulfillmentGroupApi.util.updateQueryData('getFulfillmentGroupDetails', fulfillmentGroupId, draft => {
            if (draft) {
              draft.status = 'return_delivered';
              draft.deliveredAt = new Date().toISOString();
              // Update order items from return_shipped to return_delivered
              draft.orderItems?.forEach(item => {
                if (item.status === 'return_shipped') {
                  item.status = 'return_delivered';
                }
              });
            }
          })
        );

        // Optimistic updates for individual order items
        const orderItemPatches = [];
        if (currentFG?.data?.orderItems) {
          currentFG.data.orderItems.forEach(item => {
            if (item.id && item.status === 'return_shipped') {
              const patch = dispatch(
                orderItemApi.util.updateQueryData('getOrderItemDetails', item.id, draft => {
                  if (draft) {
                    draft.status = 'return_delivered';
                  }
                })
              );
              orderItemPatches.push(patch);
            }
          });
        }

        try {
          await queryFulfilled;
        } catch (error) {
          console.error('Error in confirmReturnDelivery:', error);
          // Undo optimistic updates on error
          fgPatch.undo();
          orderItemPatches.forEach(p => p.undo());
        }
      },
      invalidatesTags: (result, error, { fulfillmentGroupId }) => [
        { type: 'FulfillmentGroup', id: fulfillmentGroupId },
        { type: 'FulfillmentGroup', id: 'LIST' },
        { type: 'Request', id: 'LIST' },
        { type: 'Order', id: 'LIST' },
        { type: 'OrderItem', id: 'LIST' },
      ],
    }),

    // Update product conditions (merchant action)
    updateProductConditions: builder.mutation({
      query: ({ fulfillmentGroupId, productUpdates }) => ({
        url: `/fulfillment-groups/${fulfillmentGroupId}/update-product-conditions`,
        method: 'POST',
        body: { productUpdates }
      }),
      invalidatesTags: (result, error, { fulfillmentGroupId }) => [
        { type: 'FulfillmentGroup', id: fulfillmentGroupId },
        { type: 'FulfillmentGroup', id: 'LIST' },
        { type: 'Product', id: 'LIST' },
        { type: 'Order', id: 'LIST' },
        { type: 'OrderItem', id: 'LIST' },
      ],
    }),

  }),
  overrideExisting: false,
});

export const {
  useGetFulfillmentGroupDetailsQuery,
  useCancelFulfillmentGroupMutation,
  useConfirmFulfillmentGroupMutation,
  useRequestGroupReturnMutation,
  useGetReturnRequestsQuery,
  useGetTrackingQuery,
  useCancelGroupReturnMutation,
  useApproveGroupReturnMutation,
  useRejectGroupReturnMutation,
  useApproveAllReturnsMutation,
  useRejectAllReturnsMutation,
  useAgreePickupMutation,
  useConfirmPickupMutation,
  useMarkReturnShippedMutation,
  useConfirmReturnDeliveryMutation,
  useUpdateProductConditionsMutation,
} = fulfillmentGroupApi; 